import { marked } from "marked";

import { ROSTER, type EntityCard } from "./_roster.generated.js";
import { TECH_DETAILS } from "./_tech.js";
import {
  RESEARCH_TREE,
  RESEARCH_ICON,
  TECH_INFO,
  type ResearchNode,
} from "../research/tree.js";

import howToPlay from "./how-to-play.md?raw";
import resources from "./resources.md?raw";
import tierProgression from "./tier-progression.md?raw";
import tier1 from "./tier-1.md?raw";
import tier2 from "./tier-2.md?raw";
import tier3 from "./tier-3.md?raw";
import tier4 from "./tier-4.md?raw";
import tier5 from "./tier-5.md?raw";

/**
 * Static docs registry. Order here is the sidebar order.
 * `parent` makes a page render as a child of another in the sidebar.
 * `minTier` hides the page on capped environments (TH_MAX_TIER < minTier).
 * Pages without a minTier always render.
 */
export type DocsPage = {
  slug: string;
  title: string;
  markdown: string;
  parent?: string;
  minTier?: number;
};

export const DOCS_PAGES: readonly DocsPage[] = [
  { slug: "how-to-play",      title: "How to Play",      markdown: howToPlay },
  { slug: "resources",        title: "Resources",        markdown: resources },
  { slug: "tier-progression", title: "Tier progression", markdown: tierProgression },
  { slug: "tier-1",           title: "Tier 1",           markdown: tier1, parent: "tier-progression" },
  { slug: "tier-2",           title: "Tier 2",           markdown: tier2, parent: "tier-progression" },
  { slug: "tier-3",           title: "Tier 3",           markdown: tier3, parent: "tier-progression", minTier: 3 },
  { slug: "tier-4",           title: "Tier 4",           markdown: tier4, parent: "tier-progression", minTier: 4 },
  { slug: "tier-5",           title: "Tier 5",           markdown: tier5, parent: "tier-progression", minTier: 5 },
];

export const DEFAULT_DOCS_SLUG = "how-to-play";

const PAGE_BY_SLUG: Map<string, DocsPage> = new Map(DOCS_PAGES.map((p) => [p.slug, p]));

function pageVisibleAt(page: DocsPage, maxTier: number): boolean {
  return (page.minTier ?? 1) <= maxTier;
}

/** Sidebar order, filtered by the env-driven tier cap. */
export function getVisibleDocsPages(maxTier: number): DocsPage[] {
  return DOCS_PAGES.filter((p) => pageVisibleAt(p, maxTier));
}

/**
 * Resolve a slug to a page, falling back to the default if the requested page
 * is hidden by the cap. Direct-URL navigation (e.g. /docs/tier-3 on a T2 env)
 * lands cleanly on the default rather than 404'ing or leaking the slug.
 */
export function findDocsPage(slug: string | null, maxTier = 5): DocsPage {
  if (slug !== null) {
    const hit = PAGE_BY_SLUG.get(slug);
    if (hit !== undefined && pageVisibleAt(hit, maxTier)) return hit;
  }
  return PAGE_BY_SLUG.get(DEFAULT_DOCS_SLUG)!;
}

type RenderedDocs = {
  html: string;
  /** H2 headings extracted post-parse, in document order. Used for in-page chip nav. */
  sections: { slug: string; title: string }[];
};

const HTML_CACHE = new Map<string, RenderedDocs>();

function slugify(text: string): string {
  return text.toLowerCase().trim().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Strip `<!-- tier-min:N -->...<!-- /tier-min -->` blocks whose N exceeds the
 * cap before passing markdown to the parser. Blocks at or below the cap have
 * their wrappers removed and inner content rendered. Used by the
 * tier-progression page so its overview table degrades cleanly on capped envs.
 * Block start matches end-of-line so adjacent blank lines stay intact and
 * Markdown still sees the surrounding paragraph/table boundaries.
 */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default:  return "&#39;";
    }
  });
}

// Bump when sprite art changes, to bypass stale CDN/browser caches that may
// have cached an HTML SPA-fallback response for a sprite URL during a window
// when the sprite didn't exist on disk.
const SPRITE_CACHE_VERSION = "14";

function renderEntityCard(entry: EntityCard): string {
  const tags = entry.tags
    .map((t) => `<span class="unit-tag">${escapeHtml(t)}</span>`)
    .join("");
  // Gate pill — uniform "Technology" label for any tower or unit gated by a
  // non-tier tech node (research-gated towers, fork picks, substitution triggers).
  // Tooltip shows the specific tech name so hovering reveals which tech is required.
  const gatePill = entry.gate !== null
    ? `<span class="unit-gate" title="Requires technology: ${escapeHtml(entry.gate.techName)}">${escapeHtml(entry.gate.label)}</span>`
    : "";
  const details = entry.details
    .map(([label, value]) =>
      `<div class="entry-detail-item"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`,
    )
    .join("");
  const spriteSrc = `/sprites/${encodeURIComponent(entry.key)}.png?v=${SPRITE_CACHE_VERSION}`;
  return `<div class="entry-card">
  <button class="entry-row" type="button" aria-expanded="false">
    <img class="entry-thumb" src="${spriteSrc}" alt="" />
    <div class="entry-meta">
      <div class="entry-name">${escapeHtml(entry.name)}${gatePill}${tags}</div>
      <div class="entry-stats">${escapeHtml(entry.summary)}</div>
    </div>
    <span class="entry-toggle" aria-hidden="true">▾</span>
  </button>
  <dl class="entry-detail" hidden>${details}</dl>
</div>`;
}

/**
 * Expand `<entity-list>` blocks in markdown into the full card HTML. Each line
 * inside the block names one ROSTER key (e.g. `arrow`, `dragon`); blank lines
 * and stray whitespace are ignored. Unknown keys render as a visible warning so
 * a typo doesn't fail silently. Runs before marked so the parser sees plain
 * HTML blocks.
 */
function expandEntityLists(markdown: string): string {
  return markdown.replace(
    /<entity-list>([\s\S]*?)<\/entity-list>/g,
    (_match, inner: string) => {
      const keys = inner
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const cards = keys
        .map((k) => {
          const entry = ROSTER[k];
          if (entry === undefined) {
            return `<div class="entry-card entry-card--missing">Unknown entity: ${escapeHtml(k)}</div>`;
          }
          return renderEntityCard(entry);
        })
        .join("\n");
      return `<div class="entry-list">\n${cards}\n</div>`;
    },
  );
}

/**
 * Render the same `.rcard` markup the in-game research panel uses
 * (apps/client/src/main.ts § renderCardSlotHtml), minus the interactive
 * `<button>` wrapper, the back-of-card decoration, and state classes
 * (`done` / `unaffordable`) — those only matter while playing.
 */
function renderInGameCard(node: ResearchNode): string {
  const info = TECH_INFO[node.id];
  const desc = info?.desc ?? "";
  const bodyHtml = desc.length > 0
    ? `<div class="rcard-body"><p class="rcard-desc">${escapeHtml(desc)}</p></div>` : "";
  const headerHtml = `<div class="rcard-header"><span class="rnode-name rcard-name">${escapeHtml(node.name)}</span></div>`;

  // All wiki-card output stays on one line — multi-line indented HTML embedded
  // inside the markdown stream trips marked's 4-space-indent code-block rule,
  // which escapes the closing tags and swallows whatever rows follow.
  if (node.choices !== undefined) {
    // Wiki preview mirrors the in-game dual-icon-top + split-bottom layout,
    // but each half is a non-interactive <div> (--preview kills hover/cursor).
    const dualIcons = node.choices.map((c) =>
      `<span class="rcard-icon icon-slot rcard-icon--dual" data-icon="${escapeHtml(c.icon)}" data-icon-size="44" aria-hidden="true"></span>`,
    ).join("");
    const halves = node.choices.map((c) =>
      `<div class="rcard-half rcard-half--preview"><span class="rcard-half-name">${escapeHtml(c.name)}</span><span class="rcard-half-desc">${escapeHtml(c.desc)}</span></div>`,
    ).join("");
    return `<div class="rcard rcard--choice">${headerHtml}<div class="rcard-art rcard-art--dual">${dualIcons}</div><div class="rcard-split">${halves}</div></div>`;
  }
  if (node.repeatable === true) {
    const iconKey = RESEARCH_ICON[node.id] ?? "";
    return `<div class="rcard rcard--repeatable">${headerHtml}<div class="rcard-art"><span class="rcard-icon icon-slot" data-icon="${escapeHtml(iconKey)}" data-icon-size="56"></span></div>${bodyHtml}</div>`;
  }
  const iconKey = RESEARCH_ICON[node.id] ?? "";
  return `<div class="rcard">${headerHtml}<div class="rcard-art"><span class="rcard-icon icon-slot" data-icon="${escapeHtml(iconKey)}" data-icon-size="56"></span></div>${bodyHtml}</div>`;
}

/** Tiny inline-markdown pass: `**bold**` for choice names, paragraph breaks. */
function renderTechDetail(text: string): string {
  return text
    .split(/\n\s*\n/)
    .map((para) => {
      const safe = escapeHtml(para.trim());
      const withBold = safe.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      return `<p>${withBold}</p>`;
    })
    .join("");
}

/** Thumb icon shown on the collapsed tech row. Forks pick the first choice's
 *  icon since the dual-icon layout doesn't fit a 56px thumb. */
function techThumbIconKey(node: ResearchNode): string {
  if (node.choices !== undefined && node.choices.length > 0) {
    return node.choices[0]!.icon;
  }
  return RESEARCH_ICON[node.id] ?? "";
}

/** One-liner shown under the name on the collapsed row. Mirrors the units /
 *  towers `entry-stats` line — just the tech-point cost. */
function techSummaryLine(node: ResearchNode): string {
  return node.tpCost === undefined ? "" : `${node.tpCost} tech pt`;
}

function renderTechRow(node: ResearchNode): string {
  const detail = TECH_DETAILS[node.id] ?? TECH_INFO[node.id]?.desc ?? "";
  const thumbKey = techThumbIconKey(node);
  const summary = techSummaryLine(node);
  // Reuse the entry-card chrome (.entry-card / .entry-row / .entry-detail) so
  // the existing accordion handler (apps/client/src/main.ts) toggles tech rows
  // exactly the same way it toggles tower / unit rows. The .entry-card--tech
  // modifier scopes the unique inner layout (card + description side-by-side).
  return `<div class="entry-card entry-card--tech">
  <button class="entry-row" type="button" aria-expanded="false">
    <span class="entry-thumb icon-slot" data-icon="${escapeHtml(thumbKey)}" data-icon-size="56" aria-hidden="true"></span>
    <div class="entry-meta">
      <div class="entry-name">${escapeHtml(node.name)}</div>
      <div class="entry-stats">${escapeHtml(summary)}</div>
    </div>
    <span class="entry-toggle" aria-hidden="true">▾</span>
  </button>
  <div class="entry-detail" hidden>
    <div class="tech-detail-inner">
      <div class="wiki-tech-card">${renderInGameCard(node)}</div>
      <div class="wiki-tech-detail">${renderTechDetail(detail)}</div>
    </div>
  </div>
</div>`;
}

/**
 * Expand `<tech-list>` blocks into collapsible tech-row HTML, mirroring the
 * `<entity-list>` accordion pattern (collapsed = thumb + name + summary;
 * expanded = the in-game research card + long-form description side-by-side).
 * Same line-per-id format as `<entity-list>`. Unknown ids render as a visible
 * warning.
 */
function expandTechLists(markdown: string): string {
  return markdown.replace(
    /<tech-list>([\s\S]*?)<\/tech-list>/g,
    (_match, inner: string) => {
      const ids = inner
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const rows = ids
        .map((id) => {
          const node = RESEARCH_TREE.find((n) => n.id === id);
          if (node === undefined) {
            return `<div class="entry-card entry-card--missing">Unknown tech: ${escapeHtml(id)}</div>`;
          }
          return renderTechRow(node);
        })
        .join("\n");
      return `<div class="entry-list">\n${rows}\n</div>`;
    },
  );
}

function preprocessTierBlocks(markdown: string, maxTier: number): string {
  return markdown.replace(
    /<!--\s*tier-min:(\d+)\s*-->\n?([\s\S]*?)<!--\s*\/tier-min\s*-->\n?/g,
    (_match, nStr: string, body: string) => {
      const n = Number(nStr);
      return Number.isInteger(n) && n <= maxTier ? body : "";
    },
  );
}

/**
 * Render the article HTML for a given page. Memoized — markdown parse runs
 * once per page+cap. Post-processes H2 tags to add anchor ids and a
 * per-section class (docs-h2--<slug>) so the CSS can color-tint them per
 * resource.
 */
export function renderDocsArticleHtml(page: DocsPage, maxTier = 5): RenderedDocs {
  const cacheKey = `${page.slug}:${maxTier}`;
  const cached = HTML_CACHE.get(cacheKey);
  if (cached !== undefined) return cached;
  const preprocessed = expandTechLists(expandEntityLists(preprocessTierBlocks(page.markdown, maxTier)));
  const raw = marked.parse(preprocessed, { async: false }) as string;
  const sections: { slug: string; title: string }[] = [];
  const html = raw.replace(/<h2>(.*?)<\/h2>/g, (_match, inner: string) => {
    const title = inner.replace(/<[^>]+>/g, "").trim();
    const slug = slugify(title);
    sections.push({ slug, title });
    return `<h2 id="${slug}" class="docs-h2 docs-h2--${slug}">${inner}</h2>`;
  });
  const result: RenderedDocs = { html, sections };
  HTML_CACHE.set(cacheKey, result);
  return result;
}
